"use strict";
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.VideoEngine = exports.IDENTIFICATION_EYES_MIN_DISTANCE = exports.REGISTRATION_EYES_MIN_DISTANCE = void 0;
const Detector_1 = require("./Detector");
const Prediction_1 = require("./Prediction");
const utils_1 = require("./utils");
exports.REGISTRATION_EYES_MIN_DISTANCE = 60;
exports.IDENTIFICATION_EYES_MIN_DISTANCE = 40;
const DEFAULT_OPTIONS = {
    backend: 'webgl',
    pose: { maxRotation: 15 },
    eyes: {
        minDistance: exports.IDENTIFICATION_EYES_MIN_DISTANCE,
        closeLowThreshold: 0.25,
        closeHighThreshold: undefined,
        maxDurationClose: 500,
    },
};
class VideoEngine {
    /**
     * @param {string} options.backend - Defaults to `webgl`.
     * @param {number} options.pose.maxRotation - Defaults to `15` deg.
     * @param {number} options.eyes.minDistance - Defaults to `60` px.
     * @param {number} options.eyes.closeLowThreshold - Defaults to `0.25`. From 0 to 1.
     * @param {number} options.eyes.closeHighThreshold - Defaults to `0.3`. From 0 to 1.
     * @param {number} options.eyes.maxDurationClose - Defaults to `500` ms.
     */
    constructor(options) {
        this._processing = (input, callback) => __awaiter(this, void 0, void 0, function* () {
            const { status, model, progressId, input: currentInput } = this._detectorData;
            const { closeHighThreshold, closeLowThreshold } = this._options.eyes;
            const { inProgress } = status;
            if (inProgress) {
                const predictions = yield model.detect(input);
                this._detectorData.status.isInitialised = true;
                let result;
                this._predictions = predictions.map((el) => {
                    const prediction = new Prediction_1.default(el, closeLowThreshold, closeHighThreshold);
                    if (!this._headPosesData.pose)
                        this._headPosesData.pose = prediction.pose;
                    result = {
                        headWasTurned: this._headPosesData.wasTurned,
                        pose: {
                            axes: prediction.pose,
                            poseInRequiredRange: this._headPosesData.poseInRequiredRange,
                        },
                        face: {
                            boundingBox: prediction.boundingBox,
                            mesh: prediction.mesh,
                            scaledMesh: prediction.scaledMesh,
                            annotations: prediction.annotations,
                            faceInViewConfidence: prediction.faceInViewConfidence,
                            eyes: {
                                wasBlinked: this._eyesData.wasBlinked,
                                eyesDistanceInRequiredRange: this._eyesData.eyesDistanceInRequiredRange,
                                isClosed: prediction.eyes.isClosed,
                                levelOpeningEyes: prediction.eyes.levelOpeningEyes,
                                isBlinked: false,
                            },
                        },
                    };
                    const isBlinked = this._isBlinked(prediction.eyes.isClosed);
                    result.face.eyes.isBlinked = isBlinked;
                    if (isBlinked) {
                        this._eyesData.wasBlinked = isBlinked;
                        result.face.eyes.wasBlinked = this._eyesData.wasBlinked;
                    }
                    const isTurned = this._isTurned(prediction.pose);
                    if (isTurned) {
                        this._headPosesData.wasTurned = isTurned;
                        result.headWasTurned = isTurned;
                    }
                    const isCorrectHeadPose = this._isCorrectHeadPoses(prediction.pose);
                    this._headPosesData.poseInRequiredRange = isCorrectHeadPose;
                    result.pose.poseInRequiredRange = isCorrectHeadPose;
                    const inAllowDistance = this._inAllowDistance(prediction.scaledMesh[468], prediction.scaledMesh[473]);
                    this._eyesData.eyesDistanceInRequiredRange = inAllowDistance;
                    result.face.eyes.eyesDistanceInRequiredRange = inAllowDistance;
                    const bestPrediction = this._getBestPredict(prediction);
                    if (bestPrediction) {
                        this._bestPrediction = Object.assign(Object.assign({}, result), { imageBase64: bestPrediction.getBase64Image(this._detectorData.input) });
                    }
                    this._currentPrediction = result;
                    return prediction;
                });
                callback && callback(result);
                this._detectorData.progressId = requestAnimationFrame(() => this._processing(currentInput, callback));
            }
            else {
                if (progressId)
                    cancelAnimationFrame(progressId);
            }
        });
        this._options = Object.assign(Object.assign(Object.assign({}, DEFAULT_OPTIONS), options), { eyes: Object.assign(Object.assign({}, DEFAULT_OPTIONS.eyes), (options && options.eyes && options.eyes)) });
        this._eyesData = {
            closedDuration: 0,
            currentDistance: 0,
            betterDistance: 0,
            wasBlinked: false,
            eyesDistanceInRequiredRange: false,
        };
        this._headPosesData = {
            isTurned: false,
            wasTurned: false,
            pose: undefined,
            poseInRequiredRange: false,
        };
        const model = new Detector_1.default(options.backend);
        this._detectorData = {
            model,
            input: undefined,
            progressId: null,
            status: {
                inProgress: false,
                isInitialised: false,
                isLoaded: false,
            },
        };
    }
    load(newBackend) {
        return __awaiter(this, void 0, void 0, function* () {
            try {
                const { model } = this._detectorData;
                const backend = newBackend || this._options.backend;
                const response = yield model.initializationDetector(backend);
                this._options.backend = backend;
                this._detectorData.status.isLoaded = true;
                return response;
            }
            catch (error) {
                console.log(error);
                return error;
            }
        });
    }
    start(input, callback) {
        return __awaiter(this, void 0, void 0, function* () {
            const { inProgress } = this._detectorData.status;
            try {
                if (!input)
                    throw new Error('Input is undefined');
                if (!inProgress) {
                    this._detectorData.status.inProgress = true;
                    this._detectorData.input = input;
                    this._processing(input, callback);
                }
                return {
                    message: 'in progress',
                    type: 'success',
                };
            }
            catch (error) {
                console.log(error);
                return {
                    message: error,
                    type: 'error',
                };
            }
        });
    }
    stop() {
        this._detectorData.status.inProgress = false;
        return this._bestPrediction;
    }
    reset() {
        this._predictions = undefined;
        this._bestPrediction = undefined;
        this._detectorData.status.inProgress = false;
        this._currentPrediction = undefined;
        this._eyesData = {
            closedDuration: 0,
            currentDistance: 0,
            betterDistance: 0,
            wasBlinked: false,
            eyesDistanceInRequiredRange: false,
        };
        this._headPosesData = {
            isTurned: false,
            wasTurned: false,
            pose: undefined,
            poseInRequiredRange: false,
        };
    }
    _isBlinked(isClosed) {
        let isBlinked = false;
        const { closedDuration } = this._eyesData;
        const { maxDurationClose } = this._options.eyes;
        if (isClosed && closedDuration === 0)
            this._eyesData.closedDuration = Date.now();
        if (!isClosed && closedDuration > 0) {
            const currentDate = Date.now();
            const diffTime = Math.abs(currentDate - closedDuration);
            isBlinked = diffTime < maxDurationClose;
            this._eyesData.closedDuration = 0;
        }
        return isBlinked;
    }
    _isTurned(newHeadPosition) {
        let isTurned = false;
        const { pose } = this._headPosesData;
        const { maxRotation } = this._options.pose;
        if (pose) {
            isTurned = Object.entries(newHeadPosition).some(([key, value]) => {
                const differenceAngles = Math.abs(value) - Math.abs(pose[key]);
                return Math.abs(differenceAngles) >= maxRotation;
            });
        }
        return isTurned;
    }
    _isCorrectHeadPoses(newPose) {
        const { maxRotation } = this._options.pose;
        let isCorrectPose = Object.values(newPose).every((angle) => Math.abs(angle) <= maxRotation);
        return isCorrectPose;
    }
    _inAllowDistance(v1, v2) {
        const distanceBetweenPupils = utils_1.distance(v1, v2);
        const { minDistance } = this._options.eyes;
        let inAllowDistance = false;
        inAllowDistance = distanceBetweenPupils >= minDistance;
        this._eyesData.currentDistance = distanceBetweenPupils;
        return inAllowDistance;
    }
    _getBestPredict(prediction) {
        const pose = prediction.pose;
        const isEyesOpen = prediction.eyes.levelOpeningEyes.left >= 0.5 && prediction.eyes.levelOpeningEyes.right >= 0.5;
        let isDistanceBetter = false;
        let isHeadPositionBetter = false;
        let bestPrediction;
        if (this._bestPrediction) {
            const coefficient = 0.2;
            isHeadPositionBetter = Object.entries(pose).every(([key, value]) => Math.abs(value) < Math.abs(this._bestPrediction.pose.axes[key]) &&
                Math.abs(value) < this._options.pose.maxRotation);
            const distanceOffset = this._eyesData.betterDistance * coefficient;
            isDistanceBetter =
                this._eyesData.currentDistance > this._eyesData.betterDistance + distanceOffset;
        }
        if (!this._bestPrediction || (isEyesOpen && (isHeadPositionBetter || isDistanceBetter))) {
            bestPrediction = prediction;
            this._eyesData.betterDistance = this._eyesData.currentDistance;
        }
        return bestPrediction;
    }
    get backend() {
        return this._options.backend;
    }
    set backend(backend) {
        this._options.backend = backend;
    }
    get inProgress() {
        return this._detectorData.status.inProgress;
    }
    get isInitialised() {
        return this._detectorData.status.isInitialised;
    }
    get isLoaded() {
        return this._detectorData.status.isLoaded;
    }
    get currentPrediction() {
        if (!this._currentPrediction.imageBase64)
            this._currentPrediction.imageBase64 = this._predictions[this._predictions.length - 1].getBase64Image(this._detectorData.input);
        return this._currentPrediction;
    }
    get bestPrediction() {
        return this._bestPrediction;
    }
    get bestShot() {
        if (this._bestPrediction)
            return this._bestPrediction.imageBase64;
        return undefined;
    }
    get predictions() {
        return this._predictions;
    }
}
exports.VideoEngine = VideoEngine;
exports.default = VideoEngine;
